/**
 * user-frontend/src/utils/api.js
 *
 * FIXES vs original:
 *  - Removidos todos os console.log de debug (token, resposta, etc.)
 *  - Timeout aumentado para 15s (ligações móveis lentas em Angola)
 *  - Redirect 401 aponta para /login (não para / como no admin)
 *  - getRecentActivity usava axios directo em vez da instância — corrigido
 *  - submitServiceRequest recebia (userId, serviceType, description) separados
 *    mas UserDashboard.js chamava com objecto — unificado para receber objecto
 *  - URLs hardcoded removidas de Profile.js, Settings.js, userActions.js
 *    (essas chamadas passam agora por esta instância via os exports abaixo)
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// ─── Instância Axios configurada ─────────────────────────────
const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // cookie httpOnly do refresh token (IAM)
});

// Injeta token em todos os pedidos
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // Num envio de ficheiros, deixar o navegador pôr o Content-Type.
    //
    // Esta instância nasce com `application/json` por omissão, e quem envia um
    // FormData sem o substituir manda um corpo multipart rotulado como JSON: o
    // multer não encontra lá ficheiro nenhum e responde «Nenhum ficheiro
    // recebido» — sem erro, sem rasto, e com o ficheiro a ter saído do
    // computador. Foi o que impediu de juntar provas às etapas.
    //
    // Apagar o cabeçalho é melhor do que escrever `multipart/form-data` à mão,
    // porque só o navegador sabe o `boundary`; escrito à mão sem ele, o corpo
    // volta a ser ilegível do outro lado. E fica aqui, e não em cada chamada,
    // porque a próxima também se ia esquecer.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Refresh silencioso (IAM Camada 1) ───────────────────────
// O access token dura 15 min; num 401 tentamos renovar UMA vez
// com o refresh token (cookie httpOnly) e repetimos o pedido.
let refreshing = null; // promessa partilhada: evita corridas de refresh

const tryRefresh = () => {
  if (!refreshing) {
    refreshing = axios
      // O mesmo cookie serve o site e o painel de administração. Dizer quem
      // somos evita receber a sessão da outra aplicação.
      .post(`${API_URL}/auth/refresh`, { audience: 'user' }, { withCredentials: true, timeout: 15000 })
      .then(({ data }) => {
        if (data.user_type && data.user_type !== 'user') {
          throw new Error('Sessão de outra aplicação');
        }
        localStorage.setItem('authToken', data.token);
        return data.token;
      })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
};

// ─── Telefone por confirmar ──────────────────────────────────
//
// O servidor recusa com 403 e `codigo: 'TELEFONE_POR_CONFIRMAR'` as acções em
// que o número tem consequência. Tratá-lo aqui, e não em cada ecrã, é o que
// impede que o próximo sítio que exija o telefone se esqueça de o fazer — e
// o que faz a pessoa ver «confirme o número» em vez de um erro genérico.
//
// `voltar` guarda o caminho de onde veio: sem isso, quem estava a meio de
// criar uma empresa era despejado no painel a ter de encontrar o caminho
// outra vez.
const irConfirmarTelefone = () => {
  const daqui = window.location.pathname + window.location.search;
  if (daqui.startsWith('/confirmar-telefone')) return;
  window.location.assign(`/confirmar-telefone?voltar=${encodeURIComponent(daqui)}`);
};

/**
 * A sessão termina uma vez, não uma vez por pedido.
 *
 * O painel abre com oito ou nove chamadas em paralelo — moradas, residências,
 * empresas, subscrições, pedidos, notificações. Quando o token expira, todas
 * falham com 401 quase ao mesmo tempo, e cada uma corria este mesmo caminho:
 * limpar o armazenamento e atribuir `window.location.href = '/login'`. Oito
 * navegações em simultâneo, umas a interromper as outras, com o React a
 * desenhar pelo meio — e o ecrã a alternar entre o painel e a entrada, em
 * ciclo. Era isso o "piscar".
 *
 * Com a bandeira, a primeira falha manda embora e as restantes ficam caladas.
 * `replace` em vez de `href` para a sessão morta não ficar no histórico: quem
 * carregasse em "voltar" caía outra vez no mesmo ciclo.
 */
let sessaoTerminada = false;

const terminarSessao = () => {
  if (sessaoTerminada) return;
  sessaoTerminada = true;
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  if (!window.location.pathname.startsWith('/login')) {
    window.location.replace('/login');
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const isAuthRoute = String(original.url || '').includes('/auth/');

    if (error.response?.status === 401 && !original._retried && !isAuthRoute) {
      original._retried = true;
      try {
        const newToken = await tryRefresh();
        original.headers = { ...original.headers, Authorization: `Bearer ${newToken}` };
        return api(original);
      } catch (_) {
        // refresh falhou → sessão terminou mesmo
      }
      terminarSessao();
    }

    if (error.response?.status === 403
        && error.response?.data?.codigo === 'TELEFONE_POR_CONFIRMAR') {
      irConfirmarTelefone();
    }

    // O mercado fechou neste separador. O ecrã lê a flag outra vez
    // e deixa de mostrar um formulário que o servidor já recusou.
    if (error.response?.status === 403
        && error.response?.data?.code === 'recruitment_mode') {
      window.dispatchEvent(new Event('dozy:recruitment-mode'));
    }

    return Promise.reject(error);
  }
);

// ════════════════════════════════════════════════════════════
// AUTENTICAÇÃO
// ════════════════════════════════════════════════════════════

export const loginUser = async (identifier, password) => {
  const { data } = await api.post('/auth/login', { identifier, password });
  return data;
};

export const logoutUser = async () => {
  const { data } = await api.post('/auth/logout', {}, { withCredentials: true });
  return data;
};

export const registerUser = async (userData) => {
  const { data } = await api.post('/auth/register', userData);
  return data;
};

export const getActiveZones = async () => {
  // 10s, não os 15 da instância: no registo, passado este tempo a lista
  // falha e a pessoa continua sem zona em vez de ficar à espera.
  const { data } = await api.get('/auth/zones', { timeout: 10000 });
  return data;
};

/** Zona do profissional, sem reenviar documentos. */
export const fixarMinhaZona = async (zoneId) => {
  const { data } = await api.put('/users/zone', { zone_id: zoneId });
  return data;
};

export const sendWorkerApprovalEmail = async (email) => {
  const { data } = await api.post('/auth/send-approval-email', { email });
  return data;
};

// ════════════════════════════════════════════════════════════
// VERIFICAÇÃO DE IDENTIDADE (workers)
// ════════════════════════════════════════════════════════════

/** formData: profile_photo, bi_photo, address_proof (File) + bi_number, address */
export const submitVerification = async (formData) => {
  const { data } = await api.post('/verification', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const getMyVerification = async () => {
  const { data } = await api.get('/verification/me');
  return data;
};

// ════════════════════════════════════════════════════════════
// PORTFÓLIO (trabalhos certificados pela Dozy)
// ════════════════════════════════════════════════════════════

export const addPortfolioItem = async (formData) => {
  const { data } = await api.post('/portfolio', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const getMyPortfolio = async () => {
  const { data } = await api.get('/portfolio/mine');
  return data;
};

export const getWorkerPortfolio = async (workerId) => {
  const { data } = await api.get(`/portfolio/worker/${workerId}`);
  return data;
};

// ════════════════════════════════════════════════════════════
// UTILIZADORES
// ════════════════════════════════════════════════════════════

export const getUserProfile = async (userId) => {
  const { data } = await api.get(`/users/${userId}`);
  return data;
};

// O login devolve token, nome e papel — não o telefone. O número fica na
// conta desde o registo (POST /users) e só se lê aqui, em GET /users/profile.
export const getMyProfile = async () => {
  const { data } = await api.get('/users/profile');
  return data;
};

// FIX: backend só tem PUT /users/profile (usa req.user.id); não existe PATCH /users/:id
export const updateUser = async (userId, payload) => {
  const { data } = await api.put('/users/profile', payload);
  return data;
};

// FIX: usa /requests/mine (deriva o cliente do token) — evita 403 quando o
// id guardado no browser está dessincronizado com a sessão.
export const getUserRequests = async () => {
  const { data } = await api.get('/requests/mine');
  return data;
};

export const getUserPastServices = async (userId) => {
  const { data } = await api.get(`/users/${userId}/past-services`);
  return data;
};

// FIX: backend é PUT /users/change-password com { currentPassword, newPassword }
// (usa req.user.id; não existe POST /auth/change-password)
export const changePassword = async (currentPassword, newPassword) => {
  const { data } = await api.put('/users/change-password', {
    currentPassword,
    newPassword,
  });
  return data;
};

// ════════════════════════════════════════════════════════════
// TRABALHADORES
// ════════════════════════════════════════════════════════════

export const getTopRatedWorkers = async (limit = 10) => {
  const { data } = await api.get('/workers/top-rated', { params: { limit } });
  // Normaliza user_id→id para os links de perfil funcionarem
  return Array.isArray(data)
    ? data.map((w) => ({ ...w, id: w.user_id ?? w.id, rating: w.average_rating ?? w.rating }))
    : [];
};

// Normaliza campos do worker para o formato esperado pela UI
const normalizeWorker = (w) => ({
  ...w,
  id: w.user_id ?? w.id,
  rating: w.average_rating ?? w.rating,
  distance: w.distance_km ?? w.distance,
});

export const getWorkerProfile = async (workerId) => {
  const { data } = await api.get(`/workers/${workerId}`);
  return normalizeWorker(data);
};

export const getWorkersByService = async (serviceId) => {
  const { data } = await api.get(`/workers/by-service/${serviceId}`);
  return Array.isArray(data) ? data.map(normalizeWorker) : [];
};
export const getBusinessesByService = async (serviceId) => {
  const { data } = await api.get(`/businesses/prestadores/por-servico/${serviceId}`);
  return Array.isArray(data) ? data : [];
};

// FIX: backend é GET /geo/nearby com { latitude, longitude, radius } e
// devolve { workers: [...] } com user_id/average_rating/distance_km
export const getNearbyWorkers = async (lat, lng, radius = 10) => {
  const { data } = await api.get('/geo/nearby', {
    params: { latitude: lat, longitude: lng, radius },
  });
  const list = Array.isArray(data) ? data : (data?.workers || []);
  return list.map(normalizeWorker);
};

// FIX: backend é GET /geo/eta?worker_id&destination_lat&destination_lng
// e devolve { estimated_time_minutes, distance_km }
export const getWorkerETA = async (workerId, lat, lng) => {
  const { data } = await api.get('/geo/eta', {
    params: { worker_id: workerId, destination_lat: lat, destination_lng: lng },
  });
  return {
    ...data,
    minutes: data?.estimated_time_minutes ?? data?.minutes,
    distance: data?.distance_km ?? data?.distance,
  };
};

// FIX: backend é POST /geo/location com { latitude, longitude }
export const updateWorkerLocation = async (lat, lng) => {
  const { data } = await api.post('/geo/location', { latitude: lat, longitude: lng });
  return data;
};

export const uploadMedia = async (workerId, formData) => {
  const { data } = await api.post(`/workers/${workerId}/media`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

// FIX: backend é POST /workers/profile-picture e o campo multipart é 'profile_picture'
export const uploadProfilePicture = async (file) => {
  const formData = new FormData();
  formData.append('profile_picture', file);
  const { data } = await api.post('/workers/profile-picture', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

// ════════════════════════════════════════════════════════════
// SOLICITAÇÕES / PEDIDOS
// ════════════════════════════════════════════════════════════

// FIX: usava /requests (TODOS os estados) → mostrava concluídos/em progresso
// nos "Trabalhos Disponíveis". /requests/open só devolve open/approved.
export const getOpenRequests = async () => {
  const { data } = await api.get('/requests/open');
  return data;
};

// FIX: recebia (userId, serviceType, description) separados
// UserDashboard chamava com objecto → unificado para receber objecto.
// Agora multipart: suporta foto anexada (pedidos de orçamento)
export const submitServiceRequest = async (payload) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') formData.append(k, v);
  });
  const { data } = await api.post('/requests', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

/** Cliente paga 100% na Dozy (escrow). metodo + telefone vão no corpo. */
export const payRequest = async (requestId, { metodo, telefone } = {}) => {
  const { data } = await api.post(`/requests/${requestId}/pay`, { metodo, telefone });
  return data;
};

/** Terminar trabalho (cliente OU worker — ambos têm de confirmar) */
export const cancelRequest = async (requestId, reason = null) => {
  const { data } = await api.post(`/requests/${requestId}/cancel`, { reason });
  return data;
};

export const finishRequest = async (requestId) => {
  const { data } = await api.post(`/requests/${requestId}/finish`);
  return data;
};

/** QR/código de chegada — cliente confirma o código que o trabalhador mostra */
export const confirmArrival = async (requestId, code) => {
  const { data } = await api.post(`/requests/${requestId}/confirm-arrival`, { code });
  return data;
};

/** Pagamentos faseados — trabalhador propõe libertar até X% (25/50/75/100) */
export const proposeRelease = async (requestId, percent) => {
  const { data } = await api.post(`/requests/${requestId}/release/propose`, { percent });
  return data;
};

/** Pagamentos faseados — cliente confirma (accept=true) ou recusa a parcela */
export const respondRelease = async (requestId, accept, reason = null) => {
  const { data } = await api.post(`/requests/${requestId}/release/respond`, { accept, reason });
  return data;
};

/** Ganhos reais do worker (payouts libertados + escrow por receber) */
export const getMyLevel = async () => {
  const { data } = await api.get('/workers/me/level');
  return data;
};

export const getMyEarnings = async () => {
  const { data } = await api.get('/requests/my-earnings');
  return data;
};

/** Trabalhos ativos do worker (contactos só após pagamento) */
export const getMyJobs = async () => {
  const { data } = await api.get('/requests/my-jobs');
  return data;
};

/** Trabalhos concluídos do worker (para adicionar ao portfólio) */
export const getMyCompletedJobs = async () => {
  const { data } = await api.get('/requests/my-completed');
  return data;
};

// FIX: /jobs/accept não existia no backend (404 "Erro ao aceitar trabalho").
// Novo modelo: o worker CANDIDATA-SE e o cliente escolhe.
// `businessId` candidata a empresa em vez da pessoa: é o perfil dela que o
// cliente vê e compara, e é ela que fica contratada.
export const applyToRequest = async (requestId, price = null, message = null, businessId = null) => {
  const { data } = await api.post(`/requests/${requestId}/apply`, {
    price, message, business_id: businessId || null,
  });
  return data;
};

export const getMyApplications = async () => {
  const { data } = await api.get('/requests/my-applications');
  return data;
};

export const getRequestApplications = async (requestId) => {
  const { data } = await api.get(`/requests/${requestId}/applications`);
  return data;
};

export const chooseWorker = async (requestId, workerId) => {
  const { data } = await api.post(`/requests/${requestId}/choose`, { workerId });
  return data;
};

/** Cliente contrapropõe um preço a um candidato */
export const counterOffer = async (requestId, workerId, counterPrice) => {
  const { data } = await api.post(`/requests/${requestId}/counter/${workerId}`, { counter_price: counterPrice });
  return data;
};

/** Worker responde à contraproposta: aceita ou propõe novo preço */
export const respondCounter = async (requestId, accept, newPrice = null) => {
  const { data } = await api.post(`/requests/${requestId}/respond-counter`, { accept, new_price: newPrice });
  return data;
};

/** Cliente marca o serviço como concluído (desbloqueia a avaliação) */
export const completeRequest = async (requestId) => {
  const { data } = await api.post(`/requests/${requestId}/complete`);
  return data;
};

// ════════════════════════════════════════════════════════════
// AVALIAÇÕES
// ════════════════════════════════════════════════════════════

// RF-008: as dimensões (qualidade, comunicação, prazo, limpeza,
// profissionalismo) seguem no `resto`. Esta função destruturava só os cinco
// campos nomeados e reconstruía o corpo a partir deles, pelo que as dimensões
// eram deitadas fora aqui, entre um ecrã que as recolhia e um servidor que as
// sabia guardar. Ninguém dava pela falta: a avaliação gravava, as estrelas
// gerais subiam, e só as colunas detalhadas ficavam eternamente a NULL.
export const addReview = async ({ booking_id, client_id, provider_id, rating, review_text, ...resto }) => {
  const { data } = await api.post('/reviews', {
    booking_id, client_id, provider_id, rating, review_text, ...resto,
  });
  return data;
};

// Reviews públicas de um trabalhador
export const getWorkerReviews = async (providerId) => {
  const { data } = await api.get('/reviews', { params: { provider_id: providerId } });
  return data;
};

// RN-054: resposta pública do trabalhador avaliado (uma, até 14 dias)
export const respondToReview = async (reviewId, response) => {
  const { data } = await api.post(`/reviews/${reviewId}/respond`, { response });
  return data;
};

// Recuperação de conta (resposta neutra — não revela se o email existe)
export const forgotPassword = async (email) => {
  const { data } = await api.post('/auth/forgot-password', { email });
  return data;
};

export const resetPassword = async (token, new_password) => {
  const { data } = await api.post('/auth/reset-password', { token, new_password });
  return data;
};

// IAM Camada 2: verificação do telefone por OTP no registo
export const verifyOtp = async (phone, otp) => {
  const { data } = await api.post('/auth/verify-otp', { phone, otp });
  return data;
};

export const resendOtp = async (phone) => {
  const { data } = await api.post('/auth/resend-otp', { phone });
  return data;
};

export const verifyEmail = async (email, code) => {
  const { data } = await api.post('/auth/verify-email', { email, code });
  return data;
};

export const resendEmailCode = async (email) => {
  const { data } = await api.post('/auth/resend-email-code', { email });
  return data;
};

// RF-015: contrato digital — abre o PDF numa nova aba (com o token da sessão)
export const openContractPdf = async (requestId) => {
  const { data } = await api.get(`/requests/${requestId}/contract/pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Spec 2.0 §11: recibo do pagamento em PDF
export const openReceiptPdf = async (requestId) => {
  const { data } = await api.get(`/requests/${requestId}/receipt/pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Fatura da taxa de plataforma cobrada ao trabalhador por libertação.
export const openWorkerInvoicePdf = async (requestId, payoutId) => {
  // Abrir já durante o clique evita o bloqueio de popups enquanto a API
  // prepara o PDF de forma assíncrona.
  const preview = window.open('', '_blank');
  if (preview) preview.opener = null;
  try {
    const { data } = await api.get(`/requests/${requestId}/worker-invoice/${payoutId}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    if (preview) preview.location.replace(url);
    else {
      const link = document.createElement('a');
      link.href = url;
      link.download = `fatura-taxa-dozy-${payoutId}.pdf`;
      link.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (error) {
    if (preview) preview.close();
    throw error;
  }
};

// Spec 2.0 §10: conversa entre cliente e trabalhador, por serviço
export const getRequestMessages = async (requestId) => {
  const { data } = await api.get(`/requests/${requestId}/messages`);
  return Array.isArray(data) ? data : [];
};

export const sendRequestMessage = async (requestId, texto) => {
  const { data } = await api.post(`/requests/${requestId}/messages`, { message_text: texto });
  return data;
};

export const getUnreadMessages = async () => {
  const { data } = await api.get('/messages/unread');
  return data;
};

// Spec 2.0 §11: estado financeiro (valor, método, referência, recibo)
export const getRequestPayment = async (requestId) => {
  const { data } = await api.get(`/requests/${requestId}/payment`);
  return data;
};

// RN-032/034: fotos de serviço antes/depois (imutáveis, hash SHA-256)
export const uploadServicePhoto = async (requestId, phase, file) => {
  const form = new FormData();
  form.append('photo', file);
  form.append('phase', phase);
  const { data } = await api.post(`/requests/${requestId}/photos`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const getServicePhotos = async (requestId) => {
  const { data } = await api.get(`/requests/${requestId}/photos`);
  return data;
};

// RN-031: percurso do trabalho (histórico imutável de estados)
export const getRequestHistory = async (requestId) => {
  const { data } = await api.get(`/requests/${requestId}/history`);
  // Devolve sempre um array: quem consome faz .map() e um 404 tratado em
  // cima chegava aqui como objecto de erro.
  return Array.isArray(data) ? data : [];
};

// ════════════════════════════════════════════════════════════
// SERVIÇOS
// ════════════════════════════════════════════════════════════

export const getServiceList = async () => {
  const { data } = await api.get('/services');
  return data;
};

// ════════════════════════════════════════════════════════════
// AGENDAMENTOS
// ════════════════════════════════════════════════════════════

export const getUserAppointments = async () => {
  const { data } = await api.get('/appointments/my');
  return data;
};

export const createRecurringAppointment = async (payload) => {
  const { data } = await api.post('/appointments/recurring', payload);
  return data;
};

export const cancelAppointment = async (id) => {
  const { data } = await api.patch(`/appointments/${id}/cancel`);
  return data;
};

export const updateAppointment = async (id, payload) => {
  const { data } = await api.patch(`/appointments/${id}`, payload);
  return data;
};

// ════════════════════════════════════════════════════════════
// DISPUTAS
// ════════════════════════════════════════════════════════════

export const getUserDisputes = async () => {
  const { data } = await api.get('/disputes/my');
  return data;
};

export const getDisputeDetails = async (disputeId) => {
  const { data } = await api.get(`/disputes/${disputeId}`);
  return data;
};

export const createDispute = async (payload) => {
  const { data } = await api.post('/disputes', payload);
  return data;
};

export const addDisputeMessage = async (disputeId, message) => {
  const { data } = await api.post(`/disputes/${disputeId}/messages`, { message });
  return data;
};

export const resolveDispute = async (disputeId, resolution) => {
  const { data } = await api.post(`/disputes/${disputeId}/resolve`, { resolution });
  return data;
};

// ════════════════════════════════════════════════════════════
// NOTIFICAÇÕES
// ════════════════════════════════════════════════════════════

export const getVapidPublicKey = async () => {
  const { data } = await api.get('/notifications/vapid-public-key');
  return data; // { publicKey, enabled }
};

export const sendTestPush = async () => {
  const { data } = await api.post('/notifications/push/test');
  return data;
};

export const subscribeToPush = async (subscription) => {
  const { data } = await api.post('/notifications/push/subscribe', subscription);
  return data;
};

export const unsubscribeFromPush = async () => {
  const { data } = await api.post('/notifications/push/unsubscribe');
  return data;
};

// ════════════════════════════════════════════════════════════
// LEVANTAMENTOS (trabalhador)
// ════════════════════════════════════════════════════════════
export const getSaldo = async (businessId = null) => {
  const { data } = await api.get('/withdrawals/balance', { params: businessId ? { business_id: businessId } : {} });
  return data;
};

/**
 * Os acertos lançados nesta conta, com o motivo escrito.
 *
 * Sem isto o saldo descia e não havia como saber porquê — e um saldo que
 * encolhe sem explicação é a maneira mais rápida de quem trabalha achar que a
 * plataforma lhe ficou com dinheiro.
 */
export const getAcertos = async (businessId = null) => {
  const { data } = await api.get('/withdrawals/acertos', { params: businessId ? { business_id: businessId } : {} });
  return Array.isArray(data) ? data : [];
};

export const getMeusLevantamentos = async (businessId = null) => {
  const { data } = await api.get('/withdrawals/mine', { params: businessId ? { business_id: businessId } : {} });
  return Array.isArray(data) ? data : [];
};

export const pedirLevantamento = async (valor, businessId = null) => {
  const { data } = await api.post('/withdrawals', businessId ? { valor, business_id: businessId } : { valor });
  return data;
};

// Como o prestador recebe (IBAN — transferência bancária)
export const getFormaRecebimento = async () => {
  const { data } = await api.get('/workers/payout-method');
  return data;
};

export const guardarFormaRecebimento = async (payload) => {
  const { data } = await api.put('/workers/payout-method', payload);
  return data;
};

// Confirmação de NIF na AGT (usada no registo, antes de haver conta)
export const consultarNif = async (nif, nome) => {
  const { data } = await api.post('/auth/nif/consultar', { nif, nome });
  return data;
};

// Atalho de navegação por profissão (a via principal é por serviço)
export const getProfissoes = async () => {
  const { data } = await api.get('/workers/professions');
  return Array.isArray(data) ? data : [];
};

export const getTrabalhadoresPorProfissao = async (profissao) => {
  const { data } = await api.get(`/workers/by-profession/${encodeURIComponent(profissao)}`);
  return Array.isArray(data) ? data : [];
};

// Serviços que o trabalhador faz (o que o torna encontrável)
export const getMeusServicos = async () => {
  const { data } = await api.get('/workers/my-services');
  return data;
};

export const guardarMeusServicos = async (payload) => {
  const { data } = await api.put('/workers/my-services', payload);
  return data;
};

// RF-003 — moradas guardadas do cliente
export const getMoradas = async () => {
  const { data } = await api.get('/addresses');
  return Array.isArray(data) ? data : [];
};

export const criarMorada = async (payload) => {
  const { data } = await api.post('/addresses', payload);
  return data;
};

export const apagarMorada = async (id) => {
  const { data } = await api.delete(`/addresses/${id}`);
  return data;
};

// RF-011 — preferências de notificação
// Histórico de notificações do próprio utilizador (spec 2.0 §13)
export const getNotifications = async (businessId = null) => {
  const { data } = await api.get('/notifications', {
    params: businessId ? { business_id: businessId } : {},
  });
  return Array.isArray(data) ? data : [];
};

export const marcarNotificacaoLida = async (id) => {
  const { data } = await api.put(`/notifications/${id}/read`);
  return data;
};

export const marcarTodasLidas = async () => {
  const { data } = await api.put('/notifications/read-all');
  return data;
};

export const getNotificationPrefs = async () => {
  const { data } = await api.get('/notifications/preferences');
  return data;
};

export const saveNotificationPrefs = async (preferencias) => {
  const { data } = await api.put('/notifications/preferences', { preferencias });
  return data;
};

// RF-008 — médias por dimensão de um trabalhador (perfil público)
export const getRatingBreakdown = async (workerId) => {
  const { data } = await api.get(`/reviews/worker/${workerId}/breakdown`);
  return data;
};

export default api;
